import { useState, useRef } from "react";

// Storage keys
const EXPLORE_STATE_STORAGE_KEY = "explorePageState";
const NAVIGATION_TRACKING_KEY = "explorePageNavigation";
const LAST_VISITED_PATH_KEY = "lastVisitedPath";
const STATE_RESTORED_TIMESTAMP_KEY = "exploreStateRestoredAt";

const useStatePreservation = (vehicles, page, hasMore, sort, selectedTag, selectedMake, selectedModel, location) => {
  const [statePreserved, setStatePreserved] = useState(false);
  const stateRestoredRef = useRef(false);
  const mountCountRef = useRef(0);
  const highestLoadedPageRef = useRef(1);
  const forceNewFetchRef = useRef(false);

  // Save page state to localStorage
  const savePageState = () => {
    try {
      const stateToSave = {
        vehicles,
        page,
        hasMore,
        scrollPosition: window.scrollY,
        sort,
        selectedTag,
        selectedMake,
        selectedModel,
        queryParams: location.search,
        timestamp: Date.now(),
      };
      
      localStorage.setItem(EXPLORE_STATE_STORAGE_KEY, JSON.stringify(stateToSave));
      console.log("ExplorePage state saved", stateToSave);
    } catch (err) {
      console.error("Error saving explore page state:", err);
    }
  };

  // Load page state from localStorage
  const loadPageState = () => {
    try {
      const savedState = localStorage.getItem(EXPLORE_STATE_STORAGE_KEY);
      
      if (!savedState) {
        console.log("No saved state found");
        return false;
      }

      const parsedState = JSON.parse(savedState);
      
      // Skip if the saved state is too old (e.g., more than 30 minutes)
      const isStateExpired = Date.now() - parsedState.timestamp > 30 * 60 * 1000;
      if (isStateExpired) {
        console.log("Saved state expired, not restoring");
        localStorage.removeItem(EXPLORE_STATE_STORAGE_KEY);
        return false;
      }

      // Don't restore if URL parameters have changed
      if (parsedState.queryParams !== location.search) {
        console.log("URL parameters changed, not restoring state");
        return false;
      }

      return parsedState;
    } catch (err) {
      console.error("Error loading explore page state:", err);
      return false;
    }
  };

  // Force application of saved state (for remounts)
  const forceSyncState = () => {
    try {
      const savedState = localStorage.getItem(EXPLORE_STATE_STORAGE_KEY);
      if (!savedState) return false;
      
      const parsedState = JSON.parse(savedState);
      return parsedState;
    } catch (err) {
      console.error("Error force syncing state:", err);
      return false;
    }
  };

  // Clear saved state
  const clearSavedState = () => {
    localStorage.removeItem(EXPLORE_STATE_STORAGE_KEY);
    console.log("Explore page state cleared");
  };

  // Track navigation to vehicle pages
  const setNavigatingToVehicle = () => {
    try {
      // Use more persistent approach with both localStorage and sessionStorage
      sessionStorage.setItem(NAVIGATION_TRACKING_KEY, "true");
      localStorage.setItem(NAVIGATION_TRACKING_KEY, "true");
      
      // Store the current path for comparison when returning
      localStorage.setItem(LAST_VISITED_PATH_KEY, window.location.pathname + window.location.search);
      
      console.log("Navigating to vehicle page, tracking set", {
        path: window.location.pathname + window.location.search
      });
      
      // Save current state before navigating
      savePageState();
    } catch (err) {
      console.error("Error setting navigation flags:", err);
    }
  };

  // Check if returning from vehicle page
  const checkReturningFromVehicle = () => {
    try {
      // Check both storage types for redundancy
      const isReturningSession = sessionStorage.getItem(NAVIGATION_TRACKING_KEY) === "true";
      const isReturningLocal = localStorage.getItem(NAVIGATION_TRACKING_KEY) === "true";
      const isReturning = isReturningSession || isReturningLocal;
      
      if (isReturning) {
        console.log("Returning from vehicle page detected");
        
        // Only clear the flags after successfully detecting the return
        sessionStorage.removeItem(NAVIGATION_TRACKING_KEY);
        localStorage.removeItem(NAVIGATION_TRACKING_KEY);
      }
      
      return isReturning;
    } catch (err) {
      console.error("Error checking if returning from vehicle:", err);
      return false;
    }
  };

  // Create a function to properly reset state for new filters
  const resetStateForNewFilters = () => {
    // Clear saved state
    clearSavedState();
    
    // Reset state restoration flags
    stateRestoredRef.current = false;
    setStatePreserved(false);
    
    // Reset page tracking
    highestLoadedPageRef.current = 0; // Set to 0 so page 1 is considered "new"
    
    // Force a fresh fetch
    forceNewFetchRef.current = true;
    
    console.log("State reset for new filter", {
      stateRestoredRef: stateRestoredRef.current,
      highestLoadedPage: highestLoadedPageRef.current,
      forceNewFetch: forceNewFetchRef.current
    });
  };

  return {
    statePreserved,
    setStatePreserved,
    stateRestoredRef,
    mountCountRef,
    highestLoadedPageRef,
    forceNewFetchRef,
    savePageState,
    loadPageState,
    forceSyncState,
    clearSavedState,
    setNavigatingToVehicle,
    checkReturningFromVehicle,
    resetStateForNewFilters
  };
};

export default useStatePreservation; 